import React, { useContext } from 'react';
import { EnvironmentFilled, InfoCircleOutlined, CopyOutlined } from '@ant-design/icons';
import CopyToClipboard from 'react-copy-to-clipboard';
import {
  Card,
  Avatar,
  Divider,
  Button,
  Table,
  Row,
  Col,
  Statistic,
  Tooltip,
  Progress,
  Descriptions,
  Badge,
  Alert,
  Collapse,
  Tag,
} from 'antd';
import { Link, useParams } from 'react-router-dom';
import gql from 'graphql-tag';
import moment from 'moment';
import Pullable from '../../../components/Pullable';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import _ from 'lodash';
import Highlighter from 'react-highlight-words';

import { COMPANY_PACKING_DAYS_GUARANTEE } from '../../../config';

import ShippingAddress from '../../../components/ShippingAddress';
import Error from '../../../components/Error';
import Loading from '../../../components/Loading';
import CountryIcon from '../../../components/CountryIcon';
import { ORDER_STATUS, ORDER_PAYMENT_STATUS, SHIPMENT_TYPE } from '../../../utils/constants';
import AppContext from '../../../components/AppContext';
import message from '../../../components/message';

import './index.css';
import { Package, QueryShippingGroupArgs, ShippingGroup, ShippingGroupStatus } from '../../../utils/types';
import { priceRangeOfShippingRoute, shortPriceDescriptionWithoutUnit } from '../../../utils/shippingRouteHelper';

const { Meta } = Card;
const { Countdown } = Statistic;

const SHIPPING_GROUP_QUERY = gql`
  query shippingGroup($id: ID!) {
    shippingGroup(id: $id) {
      id
      name
      city
      state
      country
      zipCode
      status
      type
      deadline
      notice
      countdownSeconds
      minWeight
      hasFixedLeader
      originalShippingGroupId
      isPrivate

      groupMemberships {
        user {
          code
          zipCode
        }
        canBeLeader
        reason
      }

      shippingRoute {
        id
        name
        country
        speed
        notes
        shippingMethod
        shipmentType
        dimensionalFactor
        groupIndividualMinWeight
        isActive
        canGroup
        canDirect
        priceRanges {
            startWeight
            unitPrice
            basePrice
            roundingWeight
        }
        description
      }

      leader {
        id
        name
        code
        wechatId
      }
      packages {
        id
        user {
          id
          name
          code
          wechatId
        }
        trackingNumber
        shippingCompany
        items
        notes
        status
        weight
        depth
        length
        width
      }
      order {
        id
        number
        status
        paymentStatus
        paymentMethod
        notes
        warehouseNotes

        trackingNumber
        shippedAt
        weight
        dimWeight
        chargeWeight
        totalPrice
        serviceName
        serviceProvider

        country
        state
        city
        recipientName
        phone
        address
        zipCode
      }
      totalWeight
    }
  }
`;

// const call17TrackPopup = (trackingNumber, id) => {
//   console.log('tracking', trackingNumber);
//   // @ts-ignore
//   window?.YQV5?.trackSingleF2({
//     // 必须，指定悬浮位置的元素ID。
//     YQ_ElementId: id,
//     // 可选，指定UI语言，默认根据浏览器自动识别。
//     YQ_Lang: 'zh-CN',
//     // 必须，指定要查询的单号。
//     YQ_Num: trackingNumber,
//   });
// };

function Group() {
  const { id } = useParams<{ id: string }>();

  const { user } = useContext(AppContext);

  const { loading, error, data = { shippingGroup: null }, refetch } = useQuery<{ shippingGroup: ShippingGroup }, QueryShippingGroupArgs>(SHIPPING_GROUP_QUERY, {
    variables: { id },
    fetchPolicy: 'cache-and-network',
  });

  if (loading) return <Loading />;
  if (error || !data.shippingGroup) return <Error />;

  const group = data.shippingGroup;
  const shippingRoute = group.shippingRoute;

  const packageFilter = p => group.status === ShippingGroupStatus.Open ? (p.status === 'WAREHOUSE_PENDING' || p.status === 'WAREHOUSE_RECEIVED') : p.status !== 'CANCELLED' && p.status !== 'PROBLEM';

  const packages = [...group.packages].filter(packageFilter).sort((a, b) => {
    if (user.code && a.user?.code === user.code) {
      return -1;
    } else if (user.code && b.user?.code === user.code) {
      return 1;
    } else if ((a.user?.code || '') < (b.user?.code || '')) {
      return -1;
    } else {
      return 1;
    }
  });
  const members = _.uniqWith(packages, (a, b) => a.user.code === b.user.code).map(i => i.user);
  const membersCount = members.length;

  const totalWeight = group.totalWeight || 0;
  const goalWeight = group.minWeight || 1;
  const weightPercent = Math.floor((totalWeight / goalWeight) * 100) || 0;
  const hasReachedWeight = totalWeight >= goalWeight;
  const estimatedDaysToReachWeight = 7 - Math.floor((totalWeight / goalWeight) * 7);

  // in case BE hasn't set deadline yet
  const calculatedDeadline =
    group.countdownSeconds && moment().add(group.countdownSeconds, 'seconds');
  const hasPassedDeadline =
    group.status !== 'OPEN' ||
    (group.deadline && moment().isAfter(moment(group.deadline))) ||
    (hasReachedWeight && !group.deadline && group.countdownSeconds === 0);

  const hasClosed = group.status === 'CLOSED';
  const hasCancelled = group.status === 'CANCELLED';

  const weightMap: { [key: string]: { weight: number, name: string | null | undefined }} = {};
  for (let i = 0; i < packages.length; i++) {
    const key = packages[i].user?.code;
    const weight = packages[i].weight;
    if (!weight) continue;
    if (!key) continue;
    if (key in weightMap) {
      weightMap[key].weight += weight;
    } else {
      weightMap[key] = { weight, name: packages[i].user?.name };
    }
  }

  const sortedWeight = Object.entries(weightMap)
    .map(([code, value]) => {
      const membership = group.groupMemberships.find((m) => m.user.code === code) ?? { canBeLeader: true, reason: '', user: { zipCode: '' } };
      return {
        weightNumber: value.weight,
        weight: value.weight?.toFixed(2),
        code: code,
        name: value.name,
        canBeLeader: membership.canBeLeader,
        reason: membership.reason,
        zipCode: membership.user.zipCode,
      };
    })
    .sort((a, b) => b.weightNumber - a.weightNumber);
  console.log('当前重量排序', sortedWeight);
  let count = 0;
  type RankListType = typeof sortedWeight[0] & {
    rank: number,
    predictedLeader: boolean,
    shouldShowReason: boolean,
  };
  const rankList: RankListType[] = [];
  let last;
  let hasPredictedLeader = false;
  for (let i = 0; i < sortedWeight.length; i++) {
    const entry = sortedWeight[i];
    const weight = entry.weight;

    if (weight !== last) {
      count++;
      last = weight;
    }

    const shouldPredictLeader = !hasPredictedLeader && !group.hasFixedLeader && group.status === ShippingGroupStatus.Open;

    if (entry.canBeLeader && shouldPredictLeader) {
      rankList.push({ ...entry, rank: count,  predictedLeader: true, shouldShowReason: false });
      hasPredictedLeader = true;
    } else {
      rankList.push({ ...entry, rank: count, predictedLeader: false, shouldShowReason: !hasPredictedLeader && shouldPredictLeader});
    }
    if ((count === 3 && (group.hasFixedLeader || group.status !== 'OPEN' )) || (count >= 3 && hasPredictedLeader)) break;
  }

  const tracking = group?.order?.trackingNumber?.split(/([^A-Za-z0-9]+)/).map((item, index) => {
    if (!item) return '';
    if (!item.match(/[A-Za-z]/) || !item.match(/[0-9]/)) {
      if (item.match(/[0-9]/) && item.length === 10 && (group?.order?.serviceName?.toUpperCase()?.includes('DHL') || group?.order?.serviceProvider?.toUpperCase()?.includes('DHL'))) {
        return (
          <span key={index}>
            <a
              rel="noopener noreferrer"
              href={`https://www.dhl.com/us-en/home/tracking/tracking-express.html?submit=1&tracking-id=${item}`}
              target="_blank"
            >
              {item}
            </a>
            <CopyToClipboard
              text={item}
              onCopy={() => {
                message.success('跟踪号已复制');
              }}
            >
              <span>
                &nbsp;&nbsp;
                <CopyOutlined />
              </span>
            </CopyToClipboard>
            &nbsp;&nbsp;&nbsp;
          </span>
        );
      }
      if (item.match(/[0-9]/) && item.length === 12) {
        return (
          <span key={index}>
            <a
              rel="noopener noreferrer"
              href={`https://www.fedex.com/fedextrack/?action=track&tracknumbers=${item}&locale=en_US&cntry_code=us`}
              target="_blank"
            >
              {item}
            </a>
            <CopyToClipboard
              text={item}
              onCopy={() => {
                message.success('跟踪号已复制');
              }}
            >
              <span>
                &nbsp;&nbsp;
                <CopyOutlined />
              </span>
            </CopyToClipboard>
            &nbsp;&nbsp;&nbsp;
          </span>
        );
      }
      return (
        <span style={{ whiteSpace: 'pre', wordBreak: 'break-word' }} key={index}>
          {item}
        </span>
      );
    }
    // return (
    //   <span
    //     key={index}
    //     style={{ cursor: 'pointer', color: '#EB6841' }}
    //     id={group.order.number + index}
    //     onClick={e => {
    //       e.preventDefault();
    //       call17TrackPopup(item, group.order.number + index);
    //     }}
    //   >
    //     {item}
    //   </span>
    // );
    return (
      <span key={index}>
        <a
          rel="noopener noreferrer"
          href={`https://t.17track.net/zh-cn#nums=${item}`}
          target="_blank"
        >
          {item}
        </a>
        <CopyToClipboard
          text={item}
          onCopy={() => {
            message.success('跟踪号已复制');
          }}
        >
          <span>
            &nbsp;&nbsp;
            <CopyOutlined />
          </span>
        </CopyToClipboard>
        &nbsp;&nbsp;&nbsp;
      </span>
    );
  });

  const userColumns = [
    {
      title: '会员号',
      key: 'code',
      dataIndex: 'code',
    },
    {
      title: '名字',
      key: 'name',
      dataIndex: 'name',
    },
    {
      title: '微信号',
      key: 'wechatId',
      dataIndex: 'wechatId',
    },
  ];

  type _Columns = {
    [key in keyof Package]: {
      dataIndex: key,
      render?: (value: Package[key], record: Package) => JSX.Element,
    }
  };
  type _AnyColumn = _Columns[keyof _Columns];
  type _NoColumn = {
    render: (value: string, record: Package) => JSX.Element,
  }
  type ColumnType = {
    title: string,
    key?: string,
  } & (_NoColumn | _AnyColumn);
  // } & 
  // type ColumnType = {
  //   title: string,
  //   key?: string,
  //   dataIndex?: keyof Package,
  //   render?: (value: string | number, record: Package) => JSX.Element,
  // };
  const columns: ColumnType[] = [
    {
      title: '会员',
      key: 'code',
      render: (text, record) => (
        <div style={{ background: user.code && record.user?.code === user.code ? '#ffd54f' : '' }}>
          {record.user?.name}
          <br />
          {record.user?.code}
        </div>
      ),
    },
    {
      title: '快递',
      dataIndex: 'trackingNumber',
      render: (text, record) => (
        <div>
          {record.shippingCompany}
          <br />
          {text}
        </div>
      ),
    },
    {
      title: '重量',
      dataIndex: 'weight',
      render: (weight, record) => {
        if (
          weight &&
          record['length'] &&
          record.width &&
          record.depth &&
          shippingRoute?.dimensionalFactor
        ) {
          const dimWeight =
            (Math.ceil(record['length']) * Math.ceil(record.width) * Math.ceil(record.depth)) / shippingRoute.dimensionalFactor;
          if (dimWeight > weight) {
            return (
              <div>
                <Tooltip
                  title={`该数值为预估体积重：${dimWeight.toFixed(2)}kg，入库实重：${weight.toFixed(
                    2,
                  )}kg，入库体积(cm)：${record['length']?.toFixed(0)}×${record.width?.toFixed(
                    0,
                  )}×${record.depth?.toFixed(0)}。尺寸计算根据国际标准是向上取整，如
                20.1cm 会按 21cm 计算。拼邮团所选渠道体积重计算为体积除以${
                  shippingRoute.dimensionalFactor
                }。体积重和实际重量哪个大按哪个计费`}
                >
                  <i>
                    {dimWeight?.toFixed(2)}&nbsp;
                    <InfoCircleOutlined />
                  </i>
                </Tooltip>
              </div>
            );
          }
        }
        return <div>{weight?.toFixed(2)}</div>;
      },
    },
    {
      title: '物品',
      dataIndex: 'items',
    },
  ];

  return (
    <div className="Group">
      <Pullable onRefresh={refetch}>
        <Card
          bordered={false}
          title={
            <div className="Group-title">
              <div className="Group-name">
                {group.isPrivate && '🔒 '}
                {group.name}
              </div>
              <div className="Group-subtitle">
                <EnvironmentFilled style={{ marginRight: '5px' }} />
                {group.city}, {group.state} {group.zipCode} <CountryIcon iso={group.country} />
              </div>
            </div>
          }
        >
          <Meta
            avatar={
              <Avatar src={`https://robohash.org/${group.leader ? group.leader.name : '待定'}`} />
            }
            title={
              <span>
                {`${
                  group.hasFixedLeader
                    ? '固定团长'
                    : group.status === 'OPEN'
                    ? '临时团长'
                    : '收货团长'
                }: ${group.leader ? group.leader.name : '无'}`}
                &nbsp;
                <Tooltip
                  title={
                    group.hasFixedLeader
                      ? '固定团长的收货地址是固定的，这样每次货到了，大家都可以去同一个地方取货，非常方便。长期招募固定团长，有意请详询客服 💖'
                      : '建团的人自动成为临时团长，截团时会自动把包裹总重量最大的人设为收货团长，包裹最终会寄到收货团长家'
                  }
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </span>
            }
            description={group.notice}
          />

          <Progress percent={weightPercent} showInfo={false} />
          <div className="Group-progressDesc">
            <div>
              {weightPercent}% of {goalWeight}kg
            </div>
            {hasCancelled ? (
              <div>该团已取消</div>
            ) : (
              <div>
                {!hasReachedWeight && !group.deadline && !hasClosed && (
                  <span>预计{estimatedDaysToReachWeight}天凑满重量</span>
                )}
                {!hasReachedWeight && group.deadline && !hasPassedDeadline && (
                  <Countdown
                    suffix={
                      <span>
                        截团&nbsp;
                        <Tooltip title="该团曾达到过最低重量并自动开启了截团倒计时，后来有包裹被移出导致重量不足，但倒计时仍保留，若在倒计时结束之前可以凑满重量，则仍可按时截团；否则会一直等到凑满重量再截团">
                          <QuestionCircleOutlined />
                        </Tooltip>
                      </span>
                    }
                    valueStyle={{ fontSize: '14px' }}
                    value={group.deadline}
                    format="D 天 HH:mm:ss"
                  />
                )}
                {!hasReachedWeight && group.deadline && hasPassedDeadline && !hasClosed && (
                  <span>
                    凑满重量马上截团&nbsp;
                    <Tooltip title="该团曾达到过最低重量并自动开启了截团倒计时，后来有包裹被移出导致重量不足且倒计时已结束，这种情况下等一旦凑满重量就会马上截团">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </span>
                )}
                {hasReachedWeight && !hasPassedDeadline && !hasClosed && (
                  <Countdown
                    suffix={
                      <span>
                        截团&nbsp;
                        <Tooltip title="凑满重量后会根据团设置自动开启截团倒计时，倒计时期间仍然可以跟团。但截团后再入库的包裹都无法跟团发走，会被系统自动添加到新开的同名团，后续可以修改包裹加到别的团">
                          <QuestionCircleOutlined />
                        </Tooltip>
                      </span>
                    }
                    valueStyle={{ fontSize: '14px' }}
                    value={group.deadline || calculatedDeadline}
                    format="D 天 HH:mm:ss"
                  />
                )}
                {hasReachedWeight && hasPassedDeadline && !hasClosed && (
                  <span>
                    马上截团&nbsp;
                    <Tooltip title="还有几分钟的时间可以添加已入库的包裹，但有可能会添加失败">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </span>
                )}
                {hasClosed && !group?.order?.id && (
                  <span>
                    已截团, {COMPANY_PACKING_DAYS_GUARANTEE}个工作日内打包&nbsp;
                    <Tooltip title="截团后再入库的包裹都无法跟团发走，会被系统自动添加到新开的同名团，后续可以修改包裹加到别的团">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </span>
                )}

                {group?.order?.id && <span>{ORDER_STATUS[group.order.status].text}</span>}
              </div>
            )}
          </div>

          {!hasClosed && (
            <>
              {group.isPrivate && (
                <Alert
                  type="warning"
                  style={{ margin: '5px 0' }}
                  message="该团为私人团，参团需要口令，第一次参团请先联系团长获取参团口令"
                />
              )}
              <div className="Group-actions" style={{ margin: '0.8rem 0' }}>
                <div>
                  <Link to="/tutorial">
                    <Button type="primary" ghost>
                      如何拼邮
                    </Button>
                  </Link>
                </div>

                <div>
                  <Link to={`/packages/add?groupId=${group.id}&type=GROUP&groupType=${group.type}`}>
                    <Button type="primary">添加拼邮包裹</Button>
                  </Link>
                </div>
              </div>
            </>
          )}
          {user.id === group.leader?.id && (
            <div className="Group-actions" style={{ margin: '0.8rem 0' }}>
              <div></div>
              <div>
                <Link to={`/groups/${group.id}/edit`}>
                  <Button type="primary" ghost>
                    管理拼邮团
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </Card>

        <Card bordered={false} style={{ margin: '0.8rem 0' }}>
          <Row gutter={16}>
            {shippingRoute && (
              <Col span={9}>
                <Statistic title="收费" prefix="￥" suffix="/kg" value={shippingRoute ? shortPriceDescriptionWithoutUnit(priceRangeOfShippingRoute(shippingRoute, group.minWeight || 0)) : '-'} />
              </Col>
            )}
            <Col span={5}>
              <Statistic title="团员" value={membersCount} />
            </Col>
            <Col span={5}>
              <Statistic title="总包裹" value={packages.length} />
            </Col>
            {!group?.order?.id && (
              <Col span={5}>
                <Statistic
                  title="待入库"
                  value={packages?.filter(p => p.status === 'WAREHOUSE_PENDING')?.length}
                />
              </Col>
            )}
          </Row>

          <Divider style={{ margin: '10px 0' }} />

          {!group?.order?.id && (
            <p>
              满重量后截团倒计时：
              {group.countdownSeconds === 0
                ? '满重量马上截团'
                : `${((group.countdownSeconds || 0) / 3600).toFixed(1).replace(/\.0+$/, '')}小时`}
            </p>
          )}
          <p>线路渠道：【{shippingRoute?.shippingMethod === 'SEA' ? '海运' : '空运'}】{shippingRoute?.name}</p>
          <p>货物类型：<Tag color={shippingRoute?.shipmentType? SHIPMENT_TYPE[shippingRoute.shipmentType].color : ''}>{shippingRoute?.shipmentType? SHIPMENT_TYPE[shippingRoute.shipmentType].text : ''}</Tag></p>
          {shippingRoute?.notes &&
            <p>线路须知：{shippingRoute.notes}</p>
          }
          <p>正常时效：发货后约{shippingRoute?.speed}</p>
          {shippingRoute?.dimensionalFactor ? (
            <p>
              体积重计算(kg)：长(cm)&times;宽(cm)&times;高(cm)/{shippingRoute.dimensionalFactor}
            </p>
          ) : (
            <p>体积重：该渠道通常不收取体积重费用</p>
          )}
          <p>单人最低消费重量：{shippingRoute?.groupIndividualMinWeight}kg</p>

          <Collapse ghost expandIconPosition="right">
            <Collapse.Panel key="1" header="线路描述(可运、禁运、尺寸要求)">
              <div style={{ whiteSpace: 'pre-line' }}>{shippingRoute?.description}</div>
            </Collapse.Panel>
          </Collapse>
        </Card>

        {!group?.order?.id && (
          <Card bordered={false} style={{ margin: '0.8rem 0' }}>
            <p>参团包裹邮寄地址(单击每行即可复制)：</p>
            <ShippingAddress />
          </Card>
        )}

        {group?.order?.id && (
          <Card
            title={<div className="Group-title">拼团主订单</div>}
            bordered={false}
            style={{ margin: '0.8rem 0' }}
          >
            <Descriptions title="基本信息" size="small" column={2}>
              <Descriptions.Item span={2} label="拼邮团">
                {group.name}
                <CopyToClipboard
                  text={group.name}
                  onCopy={() => {
                    message.success('团名已复制');
                  }}
                >
                  <span>
                    &nbsp;&nbsp;
                    <CopyOutlined />
                  </span>
                </CopyToClipboard>
              </Descriptions.Item>

              <Descriptions.Item label={group.hasFixedLeader ? '固定团长' : '收货团长'} span={2}>
                {group.leader?.name} {group.leader?.code}
                &nbsp;
                <Tooltip
                  title={`通常在微信群里就可以找到团长，如果找不到可以加团长微信联系，微信号：${group.leader?.wechatId}`}
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </Descriptions.Item>

              <Descriptions.Item label="订单号" span={2}>
                {group.order.number}
              </Descriptions.Item>

              <Descriptions.Item label="状态">
                {group.order.status === 'USER_RECEIVED'
                  ? `团长${ORDER_STATUS[group.order.status].text}`
                  : ORDER_STATUS[group.order.status].text}
              </Descriptions.Item>

              <Descriptions.Item label="支付状态">
                {ORDER_PAYMENT_STATUS[group.order.paymentStatus].text}
              </Descriptions.Item>
              {/* <Descriptions.Item label="支付方式">
                      {group.order.paymentMethod &&
                        ORDER_PAYMENT_METHOD[group.order.paymentMethod].text}
                    </Descriptions.Item>

                    <Descriptions.Item label="实际重量">
                      {group.order.weight && `${group.order.weight.toFixed(2)}kg`}
                    </Descriptions.Item>
                    <Descriptions.Item label="体积重量">
                      {group.order.dimWeight && `${group.order.dimWeight.toFixed(2)}kg`}
                    </Descriptions.Item>

                    <Descriptions.Item label="计费重量">
                      {group.order.chargeWeight && `${group.order.chargeWeight.toFixed(2)}kg`}
                    </Descriptions.Item>
                    <Descriptions.Item label="总费用">
                      {group.order.totalPrice && `￥${group.order.totalPrice}`}
                    </Descriptions.Item> */}

              <Descriptions.Item label="物流渠道" span={2}>
                {group.order.serviceName}
              </Descriptions.Item>

              {group.order.shippedAt && (
                <Descriptions.Item label="发货时间" span={2}>
                  {moment(group.order.shippedAt).format('YYYY-MM-DD')}
                </Descriptions.Item>
              )}

              <Descriptions.Item
                label={
                  <span>
                    跟踪号&nbsp;
                    <Tooltip title="跟踪号需要在发货几天后才有。对于海运普货，由于跟踪号属于尾程派送的公司(UPS或Fedex)，因此在包裹到达美国且被尾程派送公司提货之前物流也不会更新">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </span>
                }
                span={2}
              >
                {tracking}
              </Descriptions.Item>

              <Descriptions.Item span={2} label="仓库备注">
                <Highlighter
                  highlightStyle={{ backgroundColor: '#ffd54f', padding: 0 }}
                  searchWords={[user.code]}
                  autoEscape
                  textToHighlight={group.order.warehouseNotes || ''}
                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                />
              </Descriptions.Item>
            </Descriptions>
            <Divider />
            {group.order.status === 'SHIPPED' && (
              <Alert
                type="warning"
                message="若货已送达团长家，请提醒团长去订单页确认到货"
                style={{ marginBottom: '0.5rem' }}
              />
            )}
            <Alert
              type="warning"
              message="每箱最后一个取货的请拿走外面的大箱子，否则团长不好回收。团长属于义务劳动，请大家多尊重理解。取货后有任何问题请直接联系客服"
              style={{ marginBottom: '0.5rem' }}
            />
            <Descriptions title="收货地址" size="small" column={2}>
              <Descriptions.Item label="收件人">
                <CountryIcon iso={group.order.country} /> {group.order.recipientName}
              </Descriptions.Item>
              <Descriptions.Item label="电话">{group.order.phone}</Descriptions.Item>
              <Descriptions.Item label="地址" span={2}>
                {group.order.address}, {group.order.city}, {group.order.state},{' '}
                {group.order.country} {group.order.zipCode}
                <CopyToClipboard
                  text={group.order.address + ', ' + group.order.city}
                  onCopy={() => {
                    message.success('地址已复制');
                  }}
                >
                  <span>
                    &nbsp;&nbsp;
                    <CopyOutlined />
                  </span>
                </CopyToClipboard>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {user.id === group.leader?.id && (
          <Card
            title={<div className="Group-title">团员信息</div>}
            bordered={false}
            style={{ margin: '0.8rem 0' }}
            className="Group-items"
          >
            <Alert
              style={{ margin: '5px 10px 10px 10px' }}
              type="info"
              message={<div>该信息仅团长可见，方便联系团员</div>}
            />
            <Table
              columns={userColumns}
              dataSource={members}
              size="small"
              rowKey="id"
              pagination={false}
            />
          </Card>
        )}

        {rankList.length > 0 && (
          <Card bordered={false} title={<div className="Group-title">剁手排行榜</div>}>
            <Alert
              style={{ marginBottom: '10px' }}
              type="info"
              message={<div>以下统计按入库实重累加，非最终计费重量{!group.hasFixedLeader && group.status === ShippingGroupStatus.Open && '。如果大家都不愿意收货，默认还是最重的人收'}</div>}
            />
            {rankList.map((user) => {
              return (
                <Row key={user.code} align="middle" style={{ margin: '5px' }}>
                  {user.predictedLeader ? (
                    <Badge count={user.rank} style={{ marginRight: '5px' }} />
                  ) : (
                    <Badge
                      count={user.rank}
                      style={{ marginRight: '5px', backgroundColor: '#999' }}
                    />
                  )}
                  {user.code} {user.name} {user.weight}kg
                  {user.predictedLeader && ` (团长预测${user.zipCode ? ` 邮编${user.zipCode}` : ''})`}
                  {user.shouldShowReason && (
                    <Tooltip
                      title={user.reason}
                    >
                      &nbsp;不便收货 <QuestionCircleOutlined />
                    </Tooltip>
                  )}
                </Row>
              );
            })}
          </Card>
        )}

        <Card
          className="Group-items"
          title={<div className="Group-title">包裹列表</div>}
          bordered={false}
          style={{ margin: '0.8rem 0' }}
        >
          <Table
            columns={columns}
            dataSource={packages}
            size="small"
            rowKey="id"
            pagination={false}
          />
        </Card>
      </Pullable>
    </div>
  );
}

export default Group;
